import { useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { routePaths } from "../constants/routes";
import { jiraNotesPackId, jiraPackIncludes } from "../content/jiraNotesContent";
import { unlockPremiumAccess } from "../services/premiumNotesService";
import "../style/jira-premium-checkout.css";

const paymentMethods = [
  { id: "upi", label: "UPI", hint: "Fastest option for Indian learners" },
  { id: "card", label: "Card", hint: "Credit or debit card checkout" },
  { id: "netbanking", label: "Net Banking", hint: "Traditional bank payment" },
];

const trustPoints = [
  "Instant unlock after successful payment",
  "Printable premium PDF-style notes",
  "Advanced JQL and MNC interview answers included",
  "Premium access stays unlocked on this browser",
];

const initialForm = {
  fullName: "",
  email: "",
  phone: "",
  paymentMethod: "upi",
};

export default function JiraNotesCheckout() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [isProcessing, setIsProcessing] = useState(false);

  const orderSummary = useMemo(
    () => ({
      item: "JIRA QA Notes Pack",
      basePrice: 99,
      tax: 0,
      total: 99,
    }),
    []
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handlePurchase = (event) => {
    event.preventDefault();
    if (!form.fullName || !form.email || !form.phone) return;

    setIsProcessing(true);

    window.setTimeout(() => {
      unlockPremiumAccess(jiraNotesPackId, {
        learnerName: form.fullName,
        learnerEmail: form.email,
        learnerPhone: form.phone,
        paymentMethod: form.paymentMethod,
        amountPaid: orderSummary.total,
        productTitle: orderSummary.item,
      });
      navigate(routePaths.jiraNotesDownload, { replace: true });
    }, 1200);
  };

  return (
    <main className="premium-checkout-page">
      <section className="premium-checkout-shell">
        <div className="premium-checkout-copy">
          <span className="premium-checkout-kicker">Premium checkout</span>
          <h1>Unlock the full JIRA QA notes experience for Rs. 99</h1>
          <p>
            This premium pack is designed like a mini digital product, not just a blog page. Unlock the full notes,
            printable PDF-style version, advanced JQL, MNC interview answers, and detailed QA scenarios in one flow.
          </p>

          <div className="premium-trust-grid">
            {trustPoints.map((item) => (
              <article key={item} className="premium-trust-card">
                <strong>{item}</strong>
              </article>
            ))}
          </div>

          <div className="premium-includes-panel">
            <h2>What is included</h2>
            <ul>
              {jiraPackIncludes.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>

        <aside className="premium-checkout-card">
          <div className="premium-order-summary">
            <span>Order summary</span>
            <h2>{orderSummary.item}</h2>
            <div className="premium-price-line">
              <small>Premium notes access</small>
              <strong>Rs. {orderSummary.basePrice}</strong>
            </div>
            <div className="premium-price-line">
              <small>Taxes and fees</small>
              <strong>Rs. {orderSummary.tax}</strong>
            </div>
            <div className="premium-price-line premium-total-line">
              <small>Total payable</small>
              <strong>Rs. {orderSummary.total}</strong>
            </div>
          </div>

          <form className="premium-checkout-form" onSubmit={handlePurchase}>
            <label htmlFor="premium-name">Full Name</label>
            <input id="premium-name" name="fullName" value={form.fullName} onChange={handleChange} placeholder="Enter learner name" required />

            <label htmlFor="premium-email">Email</label>
            <input id="premium-email" type="email" name="email" value={form.email} onChange={handleChange} placeholder="Enter email" required />

            <label htmlFor="premium-phone">Phone Number</label>
            <input id="premium-phone" name="phone" value={form.phone} onChange={handleChange} placeholder="Enter phone number" required />

            <fieldset className="premium-methods">
              <legend>Payment method</legend>
              {paymentMethods.map((method) => (
                <label key={method.id} className={`premium-method-card ${form.paymentMethod === method.id ? "active" : ""}`.trim()}>
                  <input type="radio" name="paymentMethod" value={method.id} checked={form.paymentMethod === method.id} onChange={handleChange} />
                  <span>{method.label}</span>
                  <small>{method.hint}</small>
                </label>
              ))}
            </fieldset>

            <button className="premium-pay-button" type="submit" disabled={isProcessing}>
              {isProcessing ? "Processing payment..." : "Pay Rs. 99 and Unlock"}
            </button>
          </form>

          <div className="premium-checkout-footer">
            <small>This is a premium product unlock flow inside QA Coder.</small>
            <NavLink to={routePaths.jiraNotes}>Back to preview</NavLink>
          </div>
        </aside>
      </section>
    </main>
  );
}
